/**
 *
 * FormScrollButton
 *
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Icon from 'Components/Icon';

const FormScrollButtonContainer = styled.button`
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  background-color: #ff6700;
  color: white;
  font-weight: 600;
  padding: 12px 20px;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.15);
  z-index: ${({ theme }) => theme.zIndex.contactButton || 9999};
  cursor: pointer;
  transition: transform 0.3s ease-out, background-color 0.2s ease-in-out, opacity 0.3s ease-out;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
  display: none;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 15px;
  letter-spacing: 0.5px;
  animation: slideUp 0.4s ease-out;
  transform: translateY(0);
  border: none;
  opacity: 1;

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: #e55a00;
    }
  }

  &:active {
    background-color: #cc4f00;
  }

  &.hidden {
    opacity: 0;
    pointer-events: none;
    transform: translateY(100%);
  }

  @media (max-width: 768px) {
    display: flex;
    font-size: 14px;
    padding: 14px 16px;
    gap: 8px;
  }
`;

const isPackageDetailRoute = pathname =>
  pathname.includes('/promociones-en-paquetes-vacacionales/paquete/');

const FormScrollButton = () => {
  const location = useLocation();
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isFormAboveViewport, setIsFormAboveViewport] = useState(false);
  const observerRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const isPackageDetail = isPackageDetailRoute(location.pathname);

  const checkFormVisibility = useCallback(() => {
    const formElement = document.getElementById('package-contact-form');
    if (!formElement) {
      setIsFormVisible(false);
      return;
    }

    const rect = formElement.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;
    
    // También consideramos visible si cualquier parte está en el viewport
    const isPartiallyVisible = 
      rect.top < windowHeight && 
      rect.bottom > 0 && 
      rect.left < windowWidth && 
      rect.right > 0;
    
    setIsFormVisible(isPartiallyVisible);
    setIsFormAboveViewport(rect.bottom <= 0);
  }, []);

  useEffect(() => {
    if (!isPackageDetail) {
      setIsFormVisible(false);
      return;
    }

    let timeoutId = null;
    let handleScroll = null;

    // Función para configurar el observer
    const setupObserver = () => {
      const formElement = document.getElementById('package-contact-form');
      
      if (!formElement) {
        // Si el formulario no existe aún, intentar de nuevo
        timeoutId = setTimeout(() => {
          setupObserver();
        }, 300);
        return;
      }

      // Verificar visibilidad inicial
      checkFormVisibility();

      // Limpiar observer anterior si existe
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      // Configurar IntersectionObserver
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            setIsFormVisible(entry.isIntersecting);
            setIsFormAboveViewport(entry.boundingClientRect.bottom <= 0);
          });
        },
        {
          threshold: 0,
          rootMargin: '0px'
        }
      );

      observer.observe(formElement);
      observerRef.current = observer;

      // Agregar listener de scroll como respaldo con debounce
      handleScroll = () => {
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        scrollTimeoutRef.current = setTimeout(() => {
          checkFormVisibility();
        }, 50);
      };

      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('resize', checkFormVisibility, { passive: true });
    };

    setupObserver();
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (handleScroll) {
        window.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('resize', checkFormVisibility);
    };
  }, [location.pathname, isPackageDetail, checkFormVisibility]);

  const handleClick = () => {
    const formElement = document.getElementById('package-contact-form');
    if (formElement) {
      const elementPosition = formElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 200;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Solo mostrar en detalles de paquete
  if (!isPackageDetail) {
    return null;
  }

  return (
    <FormScrollButtonContainer
      onClick={handleClick}
      className={isFormVisible ? 'hidden' : ''}
      aria-label="Ir al formulario de consulta">
      <Icon
        name={isFormAboveViewport ? 'mv-arrow-up' : 'mv-arrow-down'}
        color="white"
        size={20}
      />
      Atención prioritaria completando el formulario
    </FormScrollButtonContainer>
  );
};

export default FormScrollButton;
